import { memo } from "react";
import type { BotezBokehTemplateProps } from "./types";
import { CORAL_TEXT_LARGE, FONT_BODY, FONT_DISPLAY, FONT_SCRIPT, TEAL, getEventKind } from "./utils";
import { normalizeMultilineText } from "@/utils/eventUtils";

/**
 * Godparents + parents section (the prototype's "Families"). Free-text
 * family fields go through normalizeMultilineText (JUB-99) and render with
 * whitespace-pre-line.
 */
export const BotezBokehFamilies = memo(({ event, config, t }: BotezBokehTemplateProps) => {
  const parents = normalizeMultilineText(event.family?.parents);
  const godparents = normalizeMultilineText(event.family?.godparents);

  if (!parents && !godparents) return null;

  const kind = getEventKind(event);
  const parentsLabel =
    kind.includesBaptism && event.starBaptism
      ? t("events.form.parentsLabelBaptism", { baby: event.starBaptism })
      : t("events.form.parentsLabel");

  return (
    <section id="families" className="relative z-10 py-16" style={{ background: config.palette.background }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="space-y-14">
          {godparents && (
            <div>
              <div className="text-center mb-10">
                <h2
                  className="text-4xl sm:text-5xl mb-4"
                  style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
                >
                  {t("events.form.godparentsLabel")}
                </h2>
              </div>
              <div className="text-center py-4">
                <p
                  className="text-2xl sm:text-3xl mb-3 font-bold break-words whitespace-pre-line"
                  style={{ fontFamily: FONT_DISPLAY, color: config.palette.text }}
                >
                  {godparents}
                </p>
                <div className="w-12 h-[1px] mx-auto" style={{ backgroundColor: `${TEAL}66` }} />
              </div>
            </div>
          )}

          {parents && (
            <div className={godparents ? "pt-8" : ""}>
              <div className="text-center mb-10">
                <h2
                  className="text-4xl sm:text-5xl mb-4 break-words"
                  style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
                >
                  {parentsLabel}
                </h2>
              </div>
              <div className="text-center py-4">
                <p
                  className="text-xl sm:text-2xl mb-3 font-medium break-words whitespace-pre-line"
                  style={{ fontFamily: FONT_BODY, color: config.palette.text }}
                >
                  {parents}
                </p>
                <div className="w-16 h-[1px] mx-auto" style={{ backgroundColor: `${TEAL}66` }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

BotezBokehFamilies.displayName = "BotezBokehFamilies";
